import type { Session } from '@supabase/supabase-js';
import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';
import { AppShell } from './components/AppShell';
import { Button, EmptyState } from './components/ui';
import { safeRedirect } from './features/auth/auth-helpers';
import { SignInPage } from './features/auth/SignInPage';
import { GroupsPage } from './features/groups/GroupsPage';

export interface RouterContext {
  queryClient: QueryClient;
  session: Session | null;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: () => (
    <main className="mx-auto max-w-md px-4 py-16">
      <EmptyState
        title="Page not found"
        action={
          <a href="/">
            <Button>Go home</Button>
          </a>
        }
      />
    </main>
  ),
});

const redirectSearch = z.object({ redirect: z.string().optional() });

/** Signed out screens send people who already have a session straight on. */
const skipWhenSignedIn = ({
  context,
  search,
}: {
  context: RouterContext;
  search: { redirect?: string };
}) => {
  if (context.session) throw redirect({ to: safeRedirect(search.redirect) });
};

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  validateSearch: redirectSearch,
  beforeLoad: skipWhenSignedIn,
  component: SignInPage,
});

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-up',
  validateSearch: redirectSearch,
  beforeLoad: skipWhenSignedIn,
  component: lazyRouteComponent(() => import('./features/auth/SignUpPage'), 'SignUpPage'),
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: lazyRouteComponent(
    () => import('./features/auth/ForgotPasswordPage'),
    'ForgotPasswordPage',
  ),
});

const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  component: lazyRouteComponent(
    () => import('./features/auth/AuthCallbackPage'),
    'AuthCallbackPage',
  ),
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  beforeLoad: ({ context }) => {
    if (!context.session) throw redirect({ to: '/forgot-password' });
  },
  component: lazyRouteComponent(
    () => import('./features/auth/ResetPasswordPage'),
    'ResetPasswordPage',
  ),
});

const joinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/join/$code',
  component: lazyRouteComponent(() => import('./features/join/JoinPage'), 'JoinPage'),
});

/** Everything under this layout requires a session. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: ({ context, location }) => {
    if (!context.session) throw redirect({ to: '/sign-in', search: { redirect: location.href } });
  },
  component: AppShell,
});

const lazy = <T extends Record<string, unknown>>(load: () => Promise<T>, name: keyof T & string) =>
  lazyRouteComponent(load, name);

const routes = [
  createRoute({ getParentRoute: () => appRoute, path: '/', component: GroupsPage }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/groups/new',
    component: lazy(() => import('./features/groups/NewGroupPage'), 'NewGroupPage'),
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/groups/$groupId',
    component: lazy(() => import('./features/groups/GroupPage'), 'GroupPage'),
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/groups/$groupId/members',
    component: lazy(() => import('./features/groups/MembersPage'), 'MembersPage'),
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/groups/$groupId/settings',
    component: lazy(() => import('./features/groups/GroupSettingsPage'), 'GroupSettingsPage'),
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/groups/$groupId/goals/new',
    component: lazy(() => import('./features/goals/NewGoalPage'), 'NewGoalPage'),
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/goals/$goalId',
    component: lazy(() => import('./features/goals/GoalPage'), 'GoalPage'),
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/goals/$goalId/contribute',
    component: lazy(() => import('./features/contributions/ContributePage'), 'ContributePage'),
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/payments/$attemptId',
    component: lazy(
      () => import('./features/contributions/PaymentStatusPage'),
      'PaymentStatusPage',
    ),
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/fake-checkout/$reference',
    component: lazy(() => import('./features/contributions/FakeCheckoutPage'), 'FakeCheckoutPage'),
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/profile',
    component: lazy(() => import('./features/profile/ProfilePage'), 'ProfilePage'),
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/admin',
    component: lazy(() => import('./features/admin/AdminPage'), 'AdminPage'),
  }),
] as const;

const routeTree = rootRoute.addChildren([
  signInRoute,
  signUpRoute,
  forgotPasswordRoute,
  authCallbackRoute,
  resetPasswordRoute,
  joinRoute,
  appRoute.addChildren([...routes]),
]);

export function createAppRouter(context: RouterContext) {
  return createRouter({ routeTree, context, defaultPreload: 'intent', scrollRestoration: true });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
